import { FC, useState } from 'react';
import { Avatar, Button, Dropdown, Row } from 'antd';
import { TbDots, TbMail, TbMailFast, TbMailX } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useCooldown } from '@/hooks/useCooldown';
import { isApiServiceError } from '@/services/http/errors';
import { DropdownMenuPanel, VirtualizedList as List, MiddleDot, SmallTag } from '@/shared';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useInvitationStore } from '@/stores/database/aliases/invitations';
import { WorkspaceInvitationSummary } from '@/stores/database/aliases/invitations/types';
import { ellipsisStyling, formatDate } from '@/utilities';

interface WorkspaceInvitationListItemProps {
    invitation: WorkspaceInvitationSummary;
}

const INVITATION_RESEND_COOLDOWN_SECONDS = 30;

const getInitialResendCooldownSeconds = (sentAt: Date | string): number => {
    const sentAtMilliseconds = new Date(sentAt).getTime();
    if (!Number.isFinite(sentAtMilliseconds)) {
        return 0;
    }

    return Math.max(
        0,
        Math.ceil((sentAtMilliseconds + INVITATION_RESEND_COOLDOWN_SECONDS * 1_000 - Date.now()) / 1_000),
    );
};

const getRateLimitRetryAfterSeconds = (error: unknown): number => {
    if (!isApiServiceError(error) || error.code !== 'RATE_LIMITED') {
        return INVITATION_RESEND_COOLDOWN_SECONDS;
    }

    const data = error.data as { error?: { details?: { retryAfterSeconds?: unknown } } } | undefined;
    const retryAfterSeconds = Number(data?.error?.details?.retryAfterSeconds);

    return Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
        ? Math.ceil(retryAfterSeconds)
        : INVITATION_RESEND_COOLDOWN_SECONDS;
};

const WorkspaceInvitationListItem: FC<WorkspaceInvitationListItemProps> = ({ invitation }) => {
    const [revoking, setRevoking] = useState(false);
    const [resending, setResending] = useState(false);
    const { token } = useAntDesignTheme();
    const { resendWorkspaceInvitation, revokeWorkspaceInvitation } = useInvitationStore();
    const sentAt = invitation.sentAt ?? invitation.createdAt;
    const { remainingSeconds: resendCooldown, start: startResendCooldown } = useCooldown({
        initialSeconds: getInitialResendCooldownSeconds(sentAt),
        seconds: INVITATION_RESEND_COOLDOWN_SECONDS,
    });
    const busy = resending || revoking;

    const handleResend = async () => {
        if (busy || resendCooldown > 0) {
            return;
        }

        try {
            setResending(true);
            const result = await resendWorkspaceInvitation(invitation.id);
            startResendCooldown(result.retryAfterSeconds);
            toast('Invitation Resent', {
                icon: <TbMailFast size='1rem' />,
                description: invitation.emailAddress,
            });
        } catch (error) {
            if (isApiServiceError(error) && error.code === 'RATE_LIMITED') {
                const retryAfterSeconds = getRateLimitRetryAfterSeconds(error);
                startResendCooldown(retryAfterSeconds);
                toast.warning('Please Wait Before Resending', {
                    description: `Try again in ${retryAfterSeconds} seconds.`,
                });
            } else {
                toast.error('Unable to Resend Invitation');
            }
        } finally {
            setResending(false);
        }
    };

    const handleRevoke = async () => {
        try {
            setRevoking(true);
            await revokeWorkspaceInvitation(invitation.id);
            toast('Invitation Removed', {
                icon: <TbMailX size='1rem' />,
                description: invitation.emailAddress,
            });
        } catch {
            toast.error('Unable to Remove Invitation');
        } finally {
            setRevoking(false);
        }
    };

    return (
        <List.Item
            key={invitation.id}
            extra={
                <Row align='middle' wrap={false} style={{ gap: '0.5rem' }}>
                    <SmallTag message={invitation.customRole?.name ?? invitation.permission} />
                    <Dropdown
                        trigger={['click']}
                        placement='bottomRight'
                        overlayStyle={{ minWidth: 225 }}
                        disabled={busy}
                        popupRender={() => (
                            <DropdownMenuPanel
                                items={[
                                    {
                                        key: 'resend-invitation',
                                        label:
                                            resendCooldown > 0
                                                ? `Resend Invitation in ${resendCooldown}s`
                                                : 'Resend Invitation',
                                        icon: <TbMailFast size='1rem' style={{ height: '1rem', width: '1rem' }} />,
                                        disabled: busy || resendCooldown > 0,
                                        onClick: handleResend,
                                    },
                                    { type: 'divider' },
                                    {
                                        key: 'remove-invitation',
                                        label: 'Remove Invitation',
                                        icon: <TbMailX size='1rem' />,
                                        danger: true,
                                        disabled: busy,
                                        onClick: handleRevoke,
                                    },
                                ]}
                            />
                        )}
                    >
                        <Button
                            aria-label={`Manage invitation for ${invitation.emailAddress}`}
                            type='text'
                            loading={busy}
                            disabled={busy}
                            icon={<TbDots size='1rem' style={{ verticalAlign: '-0.2rem' }} />}
                        />
                    </Dropdown>
                </Row>
            }
        >
            <List.Item.Meta
                avatar={
                    <Avatar
                        icon={<TbMail size='1rem' />}
                        style={{ marginTop: '0.375rem', color: token.colorTextTertiary }}
                    />
                }
                title={<span style={{ fontWeight: 500 }}>{invitation.emailAddress}</span>}
                description={
                    <span style={ellipsisStyling}>
                        Pending Invitation
                        <MiddleDot horizontalMargin='0.5rem' />
                        {sentAt ? `Sent ${formatDate(new Date(sentAt))}` : 'Awaiting Acceptance'}
                    </span>
                }
            />
        </List.Item>
    );
};

export default WorkspaceInvitationListItem;
